import React, { useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { LayoutDashboard, Inbox, Users, ScrollText, LogOut, Flower2 } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import { useNewCount } from "./NewCountContext";

const NAV = [
  { to: "/admin/dashboard", label: "Overblik", icon: LayoutDashboard, roles: ["admin", "medarbejder", "viewer"] },
  { to: "/admin/indbakke", label: "Indbakke", icon: Inbox, roles: ["admin", "medarbejder", "viewer"] },
  { to: "/admin/brugere", label: "Brugere", icon: Users, roles: ["admin"] },
  { to: "/admin/log", label: "Aktivitetslog", icon: ScrollText, roles: ["admin"] },
];

const AdminLayout = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const { newCount } = useNewCount();
  const navigate = useNavigate();
  const role = user?.role;

  // Discreet one-time bump only when the count INCREASES (not on first load,
  // not on decrease). Respects prefers-reduced-motion; transform-only (no layout shift).
  const bumpControls = useAnimationControls();
  const reduceMotion = useReducedMotion();
  const prevCountRef = useRef(null);
  useEffect(() => {
    if (typeof newCount === "number") {
      const prev = prevCountRef.current;
      if (prev !== null && newCount > prev && !reduceMotion) {
        bumpControls.start({ scale: [1, 1.25, 1], transition: { duration: 0.4, times: [0, 0.4, 1] } });
      }
      prevCountRef.current = newCount;
    }
  }, [newCount, reduceMotion, bumpControls]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F2EFE8] flex text-[#1A2E24]" data-testid="admin-shell">
      <aside className="w-64 shrink-0 bg-[#1A2E24] text-white flex flex-col min-h-screen sticky top-0">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
          <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#2D5A3F]">
            <Flower2 className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <div>
            <p className="font-display text-lg leading-none">Lotusgruppen</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">Adminpanel</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.filter((n) => n.roles.includes(role)).map((n) => {
            const Icon = n.icon;
            const showBadge = n.to === "/admin/indbakke" && typeof newCount === "number" && newCount > 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`admin-nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                    isActive ? "bg-[#2D5A3F] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span className="flex-1">{n.label}</span>
                {showBadge && (
                  <motion.span
                    data-testid="admin-inbox-badge"
                    aria-label={`${newCount} nye henvendelser`}
                    animate={bumpControls}
                    style={{ transformOrigin: "center" }}
                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C47C5D] text-white text-xs font-semibold leading-none"
                  >
                    {newCount > 99 ? "99+" : newCount}
                  </motion.span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-5 border-t border-white/10">
          <p className="text-xs text-white/50 mb-1">Logget ind som</p>
          <p className="text-sm truncate" data-testid="admin-current-user">{user?.email}</p>
          <p className="text-[11px] uppercase tracking-wide text-[#C9A96A] mt-0.5">{role}</p>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200 py-2 text-sm"
          >
            <LogOut className="h-4 w-4" /> Log ud
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-6 sm:px-10 py-8 max-w-6xl">{children}</main>
    </div>
  );
};

export default AdminLayout;
